/*
 * Authors: mariushirjanu
 * Created: 14/02/2019
 */
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { IObject, IWebshop } from '../../../type_definitions/datatypes';
import { WebshopService } from './webshop.service';
import { WebshopDataService } from './webshop-data.service';
import { AuthService } from '../../shared/services/auth/auth.service';
import { STATE_ACCESSMANAGEMENT, STATE_GUIDEMANAGEMENT, STATE_WEBSHOPINFO, STATE_WEBSHOPBILLING, STATE_WEBSHOPSETUP, STATE_WEBSHOPSUPPLIER, STATE_WEBSHOPB2BSETTINGS } from '../../shared/app-states';
import { SEC_WS_ACCESS, SEC_WS_B2BSETTINGS, SEC_WS_BILLING, SEC_WS_CATALOGSYNC, SEC_WS_INFO, SEC_WS_SUPPLIER } from '../../shared/services/auth/security-objects';
import { BodyClassService } from '../../shared/services/body-class.service';
import { TranslationService } from '../../shared/translation/translation.service';
import { ITranslateText } from '../../shared/wizard/wizard-wrapper.component';
import { GaWizardService } from '../../shared/wizard/ga-wizard.service';
import { GaWizard, IWizardStep } from '../../shared/wizard/ga-wizard';
import { WebshopGuideComponent } from './guide/webshop-guide.component';
import { WebshopAccessSettingsComponent } from './access-settings/webshop-access-settings.component';
import { WebshopInfoComponent } from './info/webshop-info.component';
import { WebshopBillingComponent } from './billing/webshop-billing.component';
import { WebshopSupplierComponent } from './supplier/webshop-supplier.component';
import { WebshopSetupComponent } from './setup/webshop-setup.component';
import { WIZARD_WEBSHOP } from '../../shared/app-wizards';
import { WebshopB2BSettingsComponent } from './b2b-settings/webshop-b2b-settings.component';

const CANCEL_ACTION:string = 'cancel';

@Component({
  selector: 'webshop',
  template: `
    <div *ngIf="!loading">
      <wizard-wrapper [loading]="loading" [wizardTitle]="title" [error]="error" [wizardId]="wizardId" [showAsDialog]="showAsDialog">
      </wizard-wrapper>
    </div>`,
  styleUrls: ['webshop.scss'],
  encapsulation: ViewEncapsulation.None
})
export class WebshopComponent implements OnInit, OnDestroy {
  @Input() public webshopId:string;
  @Input() public stateParams:IObject; // only relevant when the component is not run in a dialog (when the wizard should do state changes)
  @Input() public showAsDialog:boolean;
  @Output() public onCancel:EventEmitter<void> = new EventEmitter<void>();
  @Output() public onSaved:EventEmitter<Number> = new EventEmitter<Number>();
  public loading:ITranslateText;
  public error:string;
  public title:ITranslateText;

  public wizardId:string = WIZARD_WEBSHOP;
  private wizard:GaWizard;

  constructor(
    private authService:AuthService,
    private translationService:TranslationService,
    private webshopDataService:WebshopDataService,
    private webshopService:WebshopService,
    private wizardService:GaWizardService,
    bodyClassService:BodyClassService
  ) {
    bodyClassService.add('webshop');
    this.wizard = this.wizardService.getInstance(WIZARD_WEBSHOP);
  }

  public ngOnInit():void {
    this.start();
  }

  public onDialogCancel():void {
    this.onCancel.emit();
  }

  private start():void {
    this.webshopService.setWebshopId(this.webshopId);
    this.webshopService.onSaved = this.onSaved;
    this.webshopService.isInDialog = this.showAsDialog;

    if (this.webshopService.getWebshopId()) {
      this.loading = {
        translateText: 'GA.LOADING',
        translateValues: { toload: 'GA.GENERAL.WEBSHOP' }
      };
      this.webshopDataService.getWebshop(this.webshopService.getWebshopId())
        .then((webshop:IWebshop) => {
          this.webshopService.setWebshop(webshop);
          this.initWizardSteps(webshop.securedDomain);
          this.title = this.getWizardTitle(webshop.name);
        })
        .catch(() => {
          this.error = this.translationService.instant('WEBSHOP.NO_ACCESS');
        })
        .finally(() => {
          // also do this when there's an error so that users can close the dialog
          this.initWizardActions();
          this.loading = undefined;
        });
    } else {
      this.title = this.getWizardTitle();
      this.initWizardSteps();
      this.initWizardActions();
    }
  }

  private initWizardSteps(securityRole?:string):void {
    const wizardSteps:IWizardStep[] = [];

    // add the right steps to wizard
    if (this.webshopService.getWebshopId()) {
      this.authService.selectPermissions(securityRole);
      if (this.authService.viewRead(SEC_WS_INFO)) {
        wizardSteps.push(this.wizardService.createStep('infomanagement', this.translationService.instant('WEBSHOP.TABS.WEBSHOP_INFO'), WebshopInfoComponent, STATE_WEBSHOPINFO, this.stateParams, undefined, true));
      }
      if (this.authService.viewRead(SEC_WS_BILLING)) {
        wizardSteps.push(this.wizardService.createStep('webshopbilling', this.translationService.instant('WEBSHOP.TABS.BILLING'), WebshopBillingComponent, STATE_WEBSHOPBILLING, this.stateParams, undefined, true));
      }
      if (!this.showAsDialog && this.authService.viewRead(SEC_WS_SUPPLIER)) {
        wizardSteps.push(this.wizardService.createStep('webshopsupplier', this.translationService.instant('WEBSHOP.TABS.SUPPLIER'), WebshopSupplierComponent, STATE_WEBSHOPSUPPLIER, this.stateParams, undefined, true));
      }
      if (this.authService.viewRead(SEC_WS_ACCESS)) {
        wizardSteps.push(this.wizardService.createStep('accessmanagement', this.translationService.instant('WEBSHOP.TABS.ACCESS'), WebshopAccessSettingsComponent, STATE_ACCESSMANAGEMENT, this.stateParams, undefined, true));
      }
      if (!this.showAsDialog && this.authService.viewRead(SEC_WS_B2BSETTINGS)) {
        wizardSteps.push(this.wizardService.createStep('webshopb2bsettings', this.translationService.instant('WEBSHOP.TABS.B2B_SETTINGS'), WebshopB2BSettingsComponent, STATE_WEBSHOPB2BSETTINGS, this.stateParams, undefined, true));
      }
      if (!this.showAsDialog && this.authService.viewRead(SEC_WS_CATALOGSYNC)) {
        wizardSteps.push(this.wizardService.createStep('guidemanagement', this.translationService.instant('WEBSHOP.TABS.GUIDE'), WebshopGuideComponent, STATE_GUIDEMANAGEMENT, this.stateParams, undefined, true));
      }
    } else {
      // for now only possible to create new webshops
      wizardSteps.push(
        this.wizardService.createStep('webshopsetup', 'WEBSHOP.TABS.SETUP', WebshopSetupComponent, STATE_WEBSHOPSETUP, undefined, undefined, true)
      );
    }

    this.wizard.steps(wizardSteps);
    if (this.showAsDialog) {
      this.wizard.stateSynced = false;
    } else {
      this.wizard.sync();
    }
  }

  private initWizardActions() {
    if (this.showAsDialog) {
      this.wizard.addAction(
        this.wizardService.createActionButton(
          CANCEL_ACTION,
          this.translationService.instant('GA.BUTTONS.CANCEL'),
          'btn btn-danger',
          () => this.onDialogCancel()
        ));
    }
  }

  private getWizardTitle(webshopName?:string):ITranslateText {
    return {
      translateText: this.webshopService.getWebshopId()? `${webshopName}` : this.translationService.instant('WEBSHOP.TITLE.NEW_WEBSHOP', { name: '' })
    };
  }

  private clearCaches():void {
    this.wizard.clear();
    this.webshopService.clear();
  }

  public ngOnDestroy():void {
    this.clearCaches();
  }
}